// src/contexts/TierContext.tsx
// src/contexts/TierContext.tsx
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';

// PLAN = what you bought.  ROLE = who you are.  They are not the same axis.
type PlanName = 'free' | 'journey' | 'journey_pro';
type RoleName = 'applicant' | 'agency' | 'admin';
type TierName = PlanName;   // alias retained while callers migrate

interface TierContextValue {
  isLoading: boolean;
  userTier: TierName;
  routes: any[];
  checkAccess: (path: string) => boolean;
}

const TierContext = createContext<TierContextValue | undefined>(undefined);

export const useTier = () => {
  const ctx = useContext(TierContext);
  if (!ctx) throw new Error('useTier must be used inside TierProvider');
  return ctx;
};

// ─── PRODUCTION: always allow access, no upgrade redirects ───────────────────
const ENABLE_TIER_GATING = false;

export const TierProvider: React.FC<{
  children: React.ReactNode;
  userId?: string;
}> = ({ children, userId }) => {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [routes, setRoutes]       = useState<any[]>([]);
  const [userTier, setUserTier]   = useState<TierName>('basic');

  useEffect(() => {
    let mounted = true;

    const loadTierData = async () => {
      setIsLoading(true);
      try {
        // Resolve tier: user object > localStorage > default
        const resolvedTier =
          (user?.tier as TierName) ||
          (localStorage.getItem('user_tier') as TierName) ||
          'free';

        const validTiers: TierName[] = ['free', 'journey', 'journey_pro'];
        const safeTier = validTiers.includes(resolvedTier) ? resolvedTier : 'free';

        if (mounted) {
          setUserTier(safeTier);
          localStorage.setItem('user_tier', safeTier);
        }

        // Load route requirements from Supabase (used for UI gating only)
        try {
          const { data: tierRoutes, error } = await supabase
            .from('tier_routes')
            .select('*')
            .eq('is_active', true);

          if (error) throw error;
          if (mounted && tierRoutes) setRoutes(tierRoutes);
        } catch (dbError) {
          console.warn('tier_routes table unavailable, using defaults:', dbError);
          if (mounted) {
            setRoutes([
              { route_path: '/dashboard',       tier_requirements: { basic: true } },
              { route_path: '/profile',         tier_requirements: { basic: true } },
              { route_path: '/vgc',         tier_requirements: { basic: true } },
              { route_path: '/chat-premium',    tier_requirements: { premium: true } },
              { route_path: '/success-predictor', tier_requirements: { premium: true } },
              { route_path: '/quantum-leap',    tier_requirements: { premium_pro: true } },
              { route_path: '/agency',          tier_requirements: { agency: true } },
              { route_path: '/admin/dashboard', tier_requirements: { admin: true } },
            ]);
          }
        }
      } catch (error) {
        console.error('Error loading tier data:', error);
        if (mounted) setUserTier('basic');
      } finally {
        if (mounted) setIsLoading(false);
      }
    };

    loadTierData();
    return () => { mounted = false; };
  }, [user]);

  const checkAccess = (path: string): boolean => {
    // PRODUCTION: always allow — no upgrade redirects
    if (!ENABLE_TIER_GATING) return true;

    if (
      !path ||
      ['/','/dashboard','/profile','/login','/signup','/upgrade','/vgc'].includes(path)
    ) {
      return true;
    }

    if (localStorage.getItem('DEV_TIER_OVERRIDE') === 'true') return true;

    const route = routes.find(r => r.route_path === path);
    if (!route) return true; // permissive — unknown routes are allowed

    const req = route.tier_requirements || {};

    // Which plans satisfy a given requirement
    const planHierarchy: Record<PlanName, PlanName[]> = {
      free:        ['free', 'journey', 'journey_pro'],
      journey:     ['journey', 'journey_pro'],
      journey_pro: ['journey_pro'],
    };

    let requiredTier: TierName = 'basic';
    if (req.admin)       requiredTier = 'admin';
    else if (req.agency) requiredTier = 'agency';
    else if (req.premium_pro) requiredTier = 'premium_pro';
    else if (req.premium) requiredTier = 'premium';

    return tierHierarchy[requiredTier]?.includes(userTier) ?? true;
  };

  return (
    <TierContext.Provider value={{ isLoading, userTier, routes, checkAccess }}>
      {children}
    </TierContext.Provider>
  );
};